import { ApiOrganizationOrganizationTypeChoices, EhrConnectionStatus } from "@api/graphql/types-and-hooks";
import { isDirectIntegrationOrgType, isEhrConnected } from "@pages/Onboarding/ehrConnectionState";

// The homepage renders one of four states for a BCBA.
//
//   "1a" → cold-start, must connect a per-user EHR credential ("Connect your EHR")
//   "1b" → connected but no clients/reports yet ("No reports yet, start one" + guides)
//   "2"  → set up + ready, no reports yet ("Ready when you are")
//   "3"  → active: has report history ("Your Reports" list)
export type HomeState = "1a" | "1b" | "2" | "3";

// Plain signals (no hooks) so the decision tree is unit-testable in isolation,
// mirroring the onboarding EHR-state helpers in `ehrConnectionState.ts`. The
// component gathers these from useAdminData()/useEHRConnection()/queries.
export interface HomeStateSignals {
  // The BCBA has at least one report/assessment in their history.
  hasReports: boolean;
  // The BCBA/org has at least one client (learner) loaded.
  hasClients: boolean;
  // Org supports any per-user EHR scraper login (useEHRConnection().isEHREnabled).
  isEHREnabled: boolean;
  organizationType: ApiOrganizationOrganizationTypeChoices | null | undefined;
  ehrConnectionStatus: EhrConnectionStatus | null | undefined;
  // Master-credential org (one shared org-level credential — ORG_USERNAME_PASSWORD).
  isOrgUsernamePassword: boolean;
  // CentralReach data-warehouse org (org-managed credential).
  isCRDataWarehouse: boolean;
  // The user has stored EHR credentials (per-user scraper connect succeeded before).
  hasCredentials: boolean;
}

// Whether the BCBA's EHR is effectively connected for the setup stepper: a
// direct-integration org (always connected via API), a live scraper connection,
// or previously-stored credentials. This is the same notion selectHomeState uses
// to land a user in "1b", so the stepper's Step 2 ("Sync EHR") completion must be
// driven by THIS — not bare `isEhrConnected(status)` — or direct-integration /
// stored-credential users get stuck on an incomplete Step 2 with Import disabled.
export const isHomeEhrConnected = (
  s: Pick<HomeStateSignals, "organizationType" | "ehrConnectionStatus" | "hasCredentials">,
): boolean =>
  isDirectIntegrationOrgType(s.organizationType) || isEhrConnected(s.ehrConnectionStatus) || s.hasCredentials;

// Maps a BCBA's real signals to a homepage state. The cold-start stepper
// (1a/1b) is a one-time first-run experience: any report or client history
// means the BCBA already finished setup ("graduated"), so they never return to
// the stepper — even if they later disconnect their EHR (the header badge's
// Disconnect deletes credentials but leaves imported learners in place, so their
// data persists and is simply treated as stale). Reconnect/re-import then happen
// through the everyday surfaces, not here. Priority order:
//   1. Any report history → Active (3) — wins over every setup signal.
//   2. Any clients on file → Ready (2): they made it through import once, so
//      setup is complete regardless of current connection state.
//   3. No-EHR org (and no clients) → Ready (2): there's no EHR to connect and
//      clients are added manually, so we send them straight to "Start a report"
//      rather than through a setup stepper.
//   4. Org-managed EHR (master-credential / data-warehouse) → Ready (2): the org
//      import loads clients centrally, so these users are never asked to connect
//      and are treated as set up.
//   5. Per-user scraper, not yet connected → Connect-EHR cold-start (1a).
//   6. Connected but no clients yet → first-run import step (1b). This also
//      covers direct-integration orgs, which import via API from here.
export const selectHomeState = (s: HomeStateSignals): HomeState => {
  const noEhrOrg = !s.isEHREnabled && !isDirectIntegrationOrgType(s.organizationType);
  const orgManaged = s.isOrgUsernamePassword || s.isCRDataWarehouse;
  const perUserScraper = s.isEHREnabled && !orgManaged;
  const connected = isHomeEhrConnected(s);

  if (s.hasReports) return "3";
  if (s.hasClients) return "2";
  if (noEhrOrg) return "2";
  if (orgManaged) return "2";
  if (perUserScraper && !connected) return "1a";
  if (connected) return "1b";
  return "2";
};
